/**
 * Animates the keybook rack's reorder when the sort changes (FLIP): each key card
 * is read where it was before the change, then — once the new order is laid out —
 * slides from there to its new place. Only cards present in both orders move;
 * nothing animates when the person asked for reduced motion.
 */

const CARD_SELECTOR = '[data-testid="key-card"][data-entry-id]'
/** How long a card takes to slide to its new place (ms) — the one knob for the reorder's speed. */
const DURATION_MS = 1000

/** Where every key card is now, by entry id — call BEFORE the order changes. */
export function readKeyCardPlaces(root: ParentNode = document): Map<string, DOMRect> {
  const places = new Map<string, DOMRect>()
  root.querySelectorAll<HTMLElement>(CARD_SELECTOR).forEach(el => {
    const id = el.dataset.entryId
    if (id) places.set(id, el.getBoundingClientRect())
  })
  return places
}

/** Slides each card from its old place to where it is now — call AFTER the new order is laid out. */
export function slideKeyCardsFrom(before: Map<string, DOMRect>, root: ParentNode = document): void {
  if (before.size === 0) return
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  } catch { /* no matchMedia: animate */ }
  root.querySelectorAll<HTMLElement>(CARD_SELECTOR).forEach(el => {
    const was = el.dataset.entryId ? before.get(el.dataset.entryId) : undefined
    if (!was || typeof el.animate !== 'function') return
    const now = el.getBoundingClientRect()
    const dx = was.left - now.left
    const dy = was.top - now.top
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
    el.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
      { duration: DURATION_MS, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
    )
  })
}
